import { useRef, useState, useSyncExternalStore } from 'react';

import isEqual from 'lodash/isEqual';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { lensUndoAction } from '~/hooks/apis/reporting/lensUndoToast';
import { reportingLeLensQueryKeys } from '~/hooks/apis/reporting/useGetReportingLeLens';
import { carryHeldData, carryLensData, lendBlockData } from '~/hooks/apis/reporting/useGetReportingLensDocumentData';
import { refreshCertifiedShelves } from '~/hooks/apis/reporting/useLensCertification';
import {
  lensChangeFailure,
  lensChangeKey,
  lensUndoEntryKey,
  latestLensUndo,
  postLensChange,
  rememberOwnLensChange,
  toggleLensUndo,
} from '~/hooks/apis/reporting/useLensChange';

import type { Lens, LensResponse } from '~/types/leLens';

import { type BoardOperation, pageBlocks } from './editor';
import { applyLocally, figureMove, keepsData } from './localOperations';
import { asLensDocument } from './types';

export type BoardStatus = 'idle' | 'saving' | 'saved' | 'unchanged' | 'failed';

export type BoardEditor = {
  apply: (operation: BoardOperation | Array<BoardOperation>, onSettled?: (applied: boolean) => void) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  status: BoardStatus;
  pending: boolean;
  moved?: { id: string };
  touched: Array<string>;
};

export const LENS_WIDE = '*';

export const fades = (touched: Array<string> | undefined, blockId: string) => Boolean(touched?.includes(LENS_WIDE) || touched?.includes(blockId));

const touchedBy = (operations: Array<BoardOperation>) =>
  operations.flatMap((operation) => (operation.op === 'set' && operation.id === 'lens' ? [LENS_WIDE] : operation.op === 'set' || operation.op === 'form' ? [operation.id.split('/')[0]] : ['measure', 'dataset', 'filter', 'param', 'query'].includes(operation.op) ? [LENS_WIDE] : []));

type Unsaved = { operations: Array<BoardOperation>; local: boolean; onSettled?: (applied: boolean) => void };

function withoutBlockName(line: string, lens: Lens): string {
  const document = asLensDocument(lens.spec);
  const titles = document ? pageBlocks(document.page).map((block) => block.title).filter((title): title is string => typeof title === 'string' && title.length > 0) : [];
  const title = titles.sort((a, b) => b.length - a.length).find((candidate) => line.startsWith(`${candidate}:`) || line.startsWith(`${candidate} `));
  if (!title) return line;
  const rest = line.slice(title.length).replace(/^:?\s*/, '').replace(/^is now /, 'now ');
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

export const boardChangeKey = (lensId: string) => [...lensChangeKey(lensId), 'board'] as const;

export default function useBoardEdit(lensId: string, version: number | undefined): BoardEditor {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const detail = reportingLeLensQueryKeys.detail(lensId);
  const save = useMutation({
    mutationKey: boardChangeKey(lensId),
    mutationFn: (body: { baseVersion: number; operations: Array<BoardOperation> }) => postLensChange(lensId, body),
  });
  const unsaved = useRef<Array<Unsaved>>([]);
  const saved = useRef<Lens>(undefined);
  const draining = useRef(false);
  const [waiting, setWaiting] = useState(false);
  const [moved, setMoved] = useState<{ id: string }>();
  const [touched, setTouched] = useState<Array<string>>([]);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<'idle' | 'saved' | 'unchanged' | 'failed'>('idle');
  const flip = useMutation({
    mutationKey: lensChangeKey(lensId),
    mutationFn: (key: ReadonlyArray<string>) => toggleLensUndo(client, key),
    onSuccess: () => setOutcome('saved'),
    onError: (error) => {
      setOutcome('failed');
      snackbar.enqueueSnackbar(lensChangeFailure(error), { variant: 'error' });
    },
    retry: false,
  });
  const standing = useSyncExternalStore(
    (notify) => client.getQueryCache().subscribe(notify),
    () => `${latestLensUndo(client, lensId, false)?.join('/') ?? ''}|${latestLensUndo(client, lensId, true)?.join('/') ?? ''}`,
  );

  const show = (lens: Lens) => {
    const base = asLensDocument(lens.spec);
    const spec = base ? unsaved.current.reduce((document, entry) => applyLocally(document, entry.operations) ?? document, base) : lens.spec;
    client.setQueryData<LensResponse>(detail, (current) => (current ? { ...current, lens: { ...lens, spec } } : current));
    setWaiting(unsaved.current.some((entry) => !entry.local));
  };

  const drain = async () => {
    draining.current = true;
    setBusy(true);
    setOutcome('idle');
    while (unsaved.current.length && saved.current) {
      const { operations, onSettled } = unsaved.current[0];
      const base = saved.current;
      try {
        const receipt = await save.mutateAsync({ baseVersion: base.version, operations });
        unsaved.current.shift();
        rememberOwnLensChange(client, receipt);
        if (receipt.lens) {
          const document = asLensDocument(base.spec);
          if (receipt.applied && receipt.fromVersion === base.version && document && operations.every((operation) => keepsData(document, operation))) carryLensData(client, lensId, base.version, receipt.lens.version);
          else if (receipt.applied) carryHeldData(client, lensId, base.version, receipt.lens.version);
          saved.current = receipt.lens;
          show(receipt.lens);
        }
        if (receipt.applied) {
          void refreshCertifiedShelves(client);
          setOutcome('saved');
          snackbar.enqueueSnackbar(receipt.said?.length ? [...new Set(receipt.said.map((line) => withoutBlockName(line, base)))].join('. ') : 'Lens updated', {
            variant: 'success',
            action: lensUndoAction(client, lensUndoEntryKey(lensId, [receipt.toVersion]), snackbar),
          });
        } else {
          setOutcome('unchanged');
        }
        onSettled?.(receipt.applied);
      } catch (error) {
        const abandoned = unsaved.current;
        unsaved.current = [];
        show(base);
        void client.refetchQueries({ exact: true, queryKey: detail });
        setOutcome('failed');
        snackbar.enqueueSnackbar(lensChangeFailure(error), { variant: 'error' });
        for (const entry of abandoned) {
          entry.onSettled?.(false);
        }
      }
    }
    draining.current = false;
    setBusy(false);
  };

  const [undoable, redoable] = standing.split('|').map((key) => (key ? key.split('/') : undefined));
  const step = (key: Array<string> | undefined) => {
    if (key && !flip.isPending && !busy && !waiting) flip.mutate(key);
  };

  return {
    undo: () => step(undoable),
    redo: () => step(redoable),
    canUndo: Boolean(undoable) && !busy && !flip.isPending,
    canRedo: Boolean(redoable) && !busy && !flip.isPending,
    status: busy || flip.isPending ? 'saving' : outcome,
    pending: waiting,
    moved,
    touched,
    apply: (applied, onSettled) => {
      const operations = Array.isArray(applied) ? applied : [applied];
      const current = client.getQueryData<LensResponse>(detail)?.lens;
      const document = asLensDocument(current?.spec);
      if (version === undefined || !current || !document || waiting || !operations.length) {
        onSettled?.(false);
        return;
      }
      if (!draining.current) saved.current = current;
      const local = applyLocally(document, operations);
      if (local && isEqual(local, document)) {
        onSettled?.(false);
        return;
      }
      unsaved.current.push({ operations, local: Boolean(local), onSettled });
      if (local) client.setQueryData<LensResponse>(detail, (response) => (response ? { ...response, lens: { ...current, spec: local } } : response));
      for (const lent of local ? operations.map(figureMove) : []) if (lent) lendBlockData(client, lensId, current.version, lent.from, lent.to);
      setWaiting(!local);
      setTouched(operations.every((operation) => keepsData(document, operation)) ? [] : touchedBy(operations));
      setMoved(operations[0].op === 'move' ? { id: operations[0].id } : undefined);
      if (!draining.current) void drain();
    },
  };
}
